import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@app/components/core/button/Button'
import { Text } from '@app/components/core/typography/Text'
import { TagModel } from '@app/types/TagModel'
import { Style } from 'inlines'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { EditButton } from '@app/components/core/button/EditButton'

const rowStyle: Style = {
  flexWrap: 'wrap',
  gap: '0.5rem',
}

export const TagEditorPanel = ({
  title,
  currentSelectionSubtitile,
  selectNewSubtitile,
  currentTags,
  allTags,
  onSave,
}: {
  title: string
  currentSelectionSubtitile: string
  selectNewSubtitile: string
  currentTags: TagModel[]
  allTags: TagModel[]
  onSave: (tags: TagModel[]) => Promise<boolean>
}) => {
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.common
  const [editMode, setEditMode] = useState(false)
  const [selectedTags, setSelectedTags] = useState(currentTags)

  const reset = useCallback(() => {
    setSelectedTags(currentTags)
    setEditMode(false)
  }, [currentTags])

  const save = useCallback(() => {
    void onSave(selectedTags).then(reset)
  }, [selectedTags, onSave, reset])

  useEffect(() => {
    setSelectedTags(currentTags)
  }, [currentTags])

  const upForSelection = allTags.filter((t) =>
    selectedTags.every((st) => st.key !== t.key),
  )

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--palette-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title.H4 style={{ marginBottom: '0' }}>{title}</Title.H4>
        <EditButton isVisible={!editMode} onClick={() => setEditMode(true)} />
      </Row>
      {!editMode && selectedTags.length > 0 && (
        <Row style={rowStyle}>
          {selectedTags.map((tag) => (
            <Tag
              key={tag.key}
              label={tag.value}
              variant="transparent"
              size="small"
            />
          ))}
        </Row>
      )}
      {editMode && selectedTags.length > 0 && (
        <Column>
          <Text.Small isBold={true}>{currentSelectionSubtitile}</Text.Small>
          <Row style={rowStyle}>
            {selectedTags.map((tag) => (
              <Tag
                key={tag.key}
                label={tag.value}
                variant="subtle"
                size="small"
                iconRight={faClose}
                onClick={() =>
                  setSelectedTags((prev) =>
                    prev.filter((t) => t.key !== tag.key),
                  )
                }
              />
            ))}
          </Row>
        </Column>
      )}
      {editMode && allTags.length > 0 && (
        <Column>
          <Text.Small isBold={true}>{selectNewSubtitile}</Text.Small>
          <Row style={rowStyle}>
            {upForSelection.map((tag) => (
              <Tag
                key={tag.key}
                label={tag.value}
                variant="transparent"
                size="small"
                onClick={() => setSelectedTags((prev) => [...prev, tag])}
              />
            ))}
          </Row>
        </Column>
      )}
      {editMode && (
        <Row style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            label={translations.cancel}
            onClick={reset}
          />
          <Button variant="primary" label={translations.save} onClick={save} />
        </Row>
      )}
    </Column>
  )
}
